import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { fromEvent, merge, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export type IdleEvent = { type: 'countdown'; remaining: number } | { type: 'timeout' };

@Injectable({ providedIn: 'root' })
export class IdleService implements OnDestroy {
  private ngZone = inject(NgZone);
  private document = inject(DOCUMENT);

  private worker: Worker | undefined;
  private activitySub: Subscription | undefined;
  private events$ = new Subject<IdleEvent>();

  readonly idle$ = this.events$.asObservable();

  start(): void {
    this.stop();

    try {
      if (this.worker) {
        this.worker.terminate();
        this.worker = undefined;
      }
      this.worker = new Worker(new URL('../../workers/idle-user-detection.worker', import.meta.url), {
        type: 'module',
      });

      this.worker.onmessage = ({ data }: MessageEvent<string>) => {
        this.ngZone.run(() => {
          if (data.startsWith('start-countdown:')) {
            const remaining = parseInt(data.split(':')[1], 10);
            this.events$.next({ type: 'countdown', remaining });
          } else if (data === 'idleTimeout') {
            this.events$.next({ type: 'timeout' });
          }
        });
      };

      this.worker.onerror = (error) => {
        console.error('[IdleService] Worker error:', error);
      };

      this.worker.postMessage('start-idle');
      this.startActivityListener();
    } catch (error) {
      console.error('[IdleService] Failed to create worker:', error);
    }
  }

  reset(): void {
    this.worker?.postMessage('reset-idle');
  }

  stop(): void {
    if (this.worker) {
      try {
        this.worker.postMessage('stop-idle');
      } catch (error) {
        console.error('[IdleService] Error posting stop message to worker:', error);
      }
      try {
        this.worker.terminate();
      } catch (error) {
        console.error('[IdleService] Error terminating worker:', error);
      }
      this.worker = undefined;
    }
    if (this.activitySub) {
      this.activitySub.unsubscribe();
      this.activitySub = undefined;
    }
  }

  private startActivityListener(): void {
    const intentional$ = merge(
      fromEvent(this.document, 'mousedown'),
      fromEvent(this.document, 'keydown'),
      fromEvent(this.document, 'touchstart', { passive: true }),
    );

    const passive$ = fromEvent(this.document, 'scroll', {
      passive: true,
      capture: true, // catches scroll inside modals/sidebars too
    });

    this.ngZone.runOutsideAngular(() => {
      const intentionalSub = intentional$.pipe(debounceTime(500)).subscribe(() => this.reset());

      const passiveSub = passive$.pipe(debounceTime(2000)).subscribe(() => this.reset());

      this.activitySub = new Subscription();
      this.activitySub.add(intentionalSub);
      this.activitySub.add(passiveSub);
    });
  }

  ngOnDestroy(): void {
    this.stop();
    this.events$.complete();
  }
}
